import type { CircuitIR, DrcFinding } from "@models/circuit-ir";
import { Box, Chip, Stack, Typography } from "@mui/material";

import { AskAgentToFixButton } from "./AskAgentToFixButton";
import { DrcSeverityIcon } from "./DrcSeverityIcon";

interface DrcFindingItemProps {
  finding: DrcFinding;
  /**
   * Optional IR — used to render refdes / net names instead of bare UUIDs
   * for the targets the finding references. Skipped if not available.
   */
  ir?: CircuitIR | null;
  /** Snapshot id the finding was raised against — passed through to
   *  the AskAgentToFixButton so the agent operates on the right
   *  working buffer. */
  snapshotId?: string | null;
}

/**
 * One DRC `Finding` row. Per IR plan §6.7: never pre-filter by severity —
 * the caller is responsible. We render every level identically (icon +
 * code chip + message + targets); only the icon colour varies.
 */
export function DrcFindingItem({
  finding,
  ir,
  snapshotId,
}: DrcFindingItemProps) {
  const componentLabels = ir
    ? finding.component_ids
        .map((id) => ir.components.find((c) => c.id === id)?.refdes)
        .filter((r): r is string => !!r)
    : [];
  const netLabels = ir
    ? finding.net_ids
        .map((id) => ir.nets.find((n) => n.id === id)?.name)
        .filter((n): n is string => !!n)
    : [];

  return (
    <Box
      sx={{
        py: 1.25,
        px: 1.5,
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 1.5,
        alignItems: "start",
      }}
    >
      <Box sx={{ pt: 0.25 }}>
        <DrcSeverityIcon severity={finding.severity} />
      </Box>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            label={finding.code}
            variant="outlined"
            sx={{ fontFamily: "monospace", fontSize: 11 }}
          />
          {componentLabels.map((label) => (
            <Chip
              key={`c-${label}`}
              size="small"
              label={label}
              color="primary"
              variant="outlined"
              sx={{ fontFamily: "monospace", fontSize: 11 }}
            />
          ))}
          {netLabels.map((label) => (
            <Chip
              key={`n-${label}`}
              size="small"
              label={label}
              color="secondary"
              variant="outlined"
              sx={{ fontFamily: "monospace", fontSize: 11 }}
            />
          ))}
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {finding.message}
        </Typography>
        {/* Agentic fix gesture — lives inside the row so it's spatially
            tied to the finding, not floating in the panel chrome. Only
            offered on errors and warnings (info findings are
            informational by name; surfacing a "fix" CTA on them would
            be noise). */}
        {(finding.severity === "error" || finding.severity === "warning") && (
          <Box sx={{ mt: 1 }}>
            <AskAgentToFixButton
              finding={finding}
              ir={ir}
              snapshotId={snapshotId}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
